import { Chip, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import type { SxProps, Theme } from "@mui/material/styles";
import { Link, useLocation } from "react-router-dom";

import { USE_AUTH } from "../../../api/apiConfig";
import useDevMode from "../../../utils/devmode";
import {
  FEATURE_FLAG_KEYS,
  type FeatureFlagValue,
  useFeatureFlags,
} from "../../../utils/featureFlags";

interface FeatureType {
  display: string;
  path: string;
  beta: boolean;
  feature?: string;
  devOnly?: boolean;
  featureFlag?: string;
}

const DASHBOARDS: Record<string, FeatureType> = {
  Copilot: {
    display: "Copilot",
    path: "Copilot",
    beta: false,
  },
  Payments: {
    display: "Insurance Payments",
    path: "Payments",
    beta: false,
  },
  Claims: {
    display: "Claims",
    path: "Claims",
    beta: false,
  },
  InsuranceAr: {
    display: "Insurance A/R",
    path: "insurance-ar",
    beta: false,
    featureFlag: FEATURE_FLAG_KEYS.INSURANCE_AR,
  },
  Patients: {
    display: "Patients",
    path: "Patients",
    beta: false,
  },
  Credentials: {
    display: "Credentials",
    path: "Credentials",
    beta: false,
  },
  Reports: {
    display: "Reports",
    path: "Reports",
    beta: false,
    featureFlag: FEATURE_FLAG_KEYS.REPORTS,
  },
  Reconciliation: {
    display: "Reconciliation",
    path: "Reconciliation",
    beta: false,
    featureFlag: FEATURE_FLAG_KEYS.BANK_RECONCILIATION,
  },
  InsuranceVerification: {
    display: "Verification",
    path: "insurance-verification",
    beta: false,
    featureFlag: FEATURE_FLAG_KEYS.INSURANCE_VERIFICATION,
  },
  Studio: {
    display: "Studio",
    path: "studio",
    beta: true,
    devOnly: true,
  },
};

interface DashboardItemProps {
  formattedPath: string;
  toPath: string;
  display: string;
  isSelected: boolean;
  beta: boolean;
  isNavOpen: boolean;
}

function DashboardItem({
  formattedPath,
  display,
  beta,
  toPath,
  isSelected,
  isNavOpen,
}: DashboardItemProps) {
  const iconPath = `/static/images/${formattedPath}-icon.svg`;
  const buttonSXOverride: SxProps<Theme> = {
    borderRadius: "12px",
    justifyContent: isNavOpen ? "left" : "center",
    padding: "8px 12px",
    width: "100%",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: isSelected ? "#E9D7D9" : "#F9FAFB",
    backgroundColor: isSelected ? "#1E1D31" : "#25233F",
    ":hover": { backgroundColor: "#1E1D31" },
    minWidth: 0,
  };

  const button = (
    <Link style={{ textDecoration: "none" }} to={toPath}>
      <IconButton aria-label={display} sx={buttonSXOverride}>
        <img
          style={{ marginRight: isNavOpen ? ".75rem" : 0 }}
          src={iconPath}
          alt={`${display} Icon`}
          width="24"
          height="24"
        />
        {isNavOpen && display}
        {isNavOpen && beta ? (
          <Chip
            label="Beta"
            size="small"
            sx={(theme) => ({
              border: `1px solid ${theme.palette.background.paper}`,
              color: theme.palette.background.paper,
              marginLeft: "auto",
            })}
          />
        ) : null}
      </IconButton>
    </Link>
  );

  return (
    <Box
      component="ul"
      sx={{ width: "100%", maxWidth: isNavOpen ? "230px" : "48px" }}
    >
      {isNavOpen ? (
        button
      ) : (
        <Tooltip title={display} placement="right">
          {button}
        </Tooltip>
      )}
    </Box>
  );
}

interface DashboardListProps {
  practices: PracticeMessage[];
  isNavOpen: boolean;
}

export function DashboardList({ practices, isNavOpen }: DashboardListProps) {
  const devMode = useDevMode();
  const { featureFlags, isLoading } = useFeatureFlags();
  const location = useLocation();
  const { search } = location;

  if (isLoading) return null;

  const dashboard = Object.entries(DASHBOARDS).reduce(
    (acc, [key, item]) => {
      if (item.devOnly && !devMode && USE_AUTH) return acc;

      if (
        item.featureFlag &&
        !featureFlags[item.featureFlag as FeatureFlagValue]
      ) {
        return acc;
      }

      if (
        item.feature === "enablePostie" &&
        !practices.some((p) => p.enabledPostie)
      )
        return acc;

      acc[key] = item;
      return acc;
    },
    {} as Record<string, FeatureType>,
  );

  return (
    <Box
      component="ul"
      sx={{ display: "flex", flexDirection: "column", gap: ".5rem" }}
    >
      {Object.keys(dashboard).map((key) => {
        const currentDashboardItem = dashboard[key];
        const { path, display, beta } = currentDashboardItem;

        const formattedPath = path.toLowerCase();
        const toPath = `/${formattedPath}${search}`;
        const isSelected = location.pathname.startsWith(`/${formattedPath}`);

        return (
          <DashboardItem
            key={formattedPath}
            isSelected={isSelected}
            toPath={toPath}
            beta={beta}
            formattedPath={formattedPath}
            display={display}
            isNavOpen={isNavOpen}
          />
        );
      })}
    </Box>
  );
}
